import { useDispatch, useSelector } from 'react-redux';
import { AppState, UserProfile, UserState } from 'types';
import { useCallback, useState, MouseEvent } from 'react';
import * as actions from 'store/actions';
import { createManagedAccount, submitProfile } from 'store/thunks';
import { addCurrentTimeToDateString, checkValidity, formatDateForApi } from 'shared/utility';
import { updateObject } from 'shared/util';
import _get from 'lodash/get';
import _forEach from 'lodash/forEach';

interface ElementConfig {
  type: string;
  placeholder?: string;
  readOnly: boolean;
  guidetext?: string;
}

interface ElementValidation {
  required: boolean;
  validationError?: boolean;
}

export interface ProfileFormState {
  controls: {
    profileImageId: {
      elementType: string;
      elementConfig: ElementConfig;
      value: string;
      validation: ElementValidation;
      valid: boolean;
      touched: boolean;
    };
    firstname: {
      elementType: string;
      elementConfig: ElementConfig;
      value: string;
      validation: ElementValidation;
      touched: boolean;
      valid: boolean;
    };
    lastname: {
      elementType: string;
      elementConfig: ElementConfig;
      value: string;
      validation: ElementValidation;
      touched: boolean;
      valid: boolean;
    };
    nickname: {
      elementType: string;
      elementConfig: ElementConfig;
      value: string;
      validation: ElementValidation;
      touched: boolean;
      valid: boolean;
    };
    birthdate: {
      elementType: string;
      elementConfig: ElementConfig;
      value: string;
      validation: ElementValidation;
      touched: boolean;
      valid: boolean;
    };
    color: {
      elementType: string;
      elementConfig: ElementConfig;
      value: string;
      validation: ElementValidation;
      touched: boolean;
      valid: boolean;
    };
  };
  profileThumbUrl: string;
  showFileTypeError: boolean;
  showColorSelector: boolean;
  showCloseFormDialogue: boolean;
  showBirthdatePicker: boolean;
  loadingImage: boolean;
  formIsValid: boolean;
  formTouched: boolean;
  attemptedFormSubmission: boolean;
  showDeleteManagedUserConfirmation?: boolean;
  showDeletePostConfirmation?: boolean;
}

interface Callbacks {
  handleColorSelection: (val: string) => void;
  handleImageUpload: (event: MouseEvent<HTMLInputElement>) => void;
  hideFileTypeErrorDialogue: VoidFunction;
  handleColorSelectorToggle: VoidFunction;
  handleImageUpdate: VoidFunction;
  handleDateSelect: (val: string) => void;
  inputChangedHandler: (event: { target: HTMLInputElement }, val: keyof ProfileFormState['controls']) => void;
  submitHandler: VoidFunction;
  toggleDeleteManagedUserConfirmation: VoidFunction;
  handleManagedUserAccountDeletion: VoidFunction;
  handleProfileFormClose: VoidFunction;
  toggleProfileCloseDialogue: VoidFunction;
  toggleBirthdatePicker: VoidFunction;
}

interface UseProfileForm {
  (): { state: ProfileFormState; callbacks: Callbacks; isManagedUser: boolean; profileCreated: boolean; editingProfile: UserProfile };
}

const useProfileForm: UseProfileForm = () => {
  const { editingProfile, signedInAs } = useSelector<AppState, UserState>((state) => state.user);
  const [state, setState] = useState<ProfileFormState>({
    controls: {
      profileImageId: {
        elementType: 'input',
        elementConfig: {
          type: 'file',
          placeholder: '',
          readOnly: true,
        },
        value: '',
        validation: {
          required: false,
        },
        valid: false,
        touched: false,
      },
      firstname: {
        elementType: 'input',
        elementConfig: {
          type: 'text',
          placeholder: 'First name*',
          readOnly: false,
        },
        value: editingProfile.FirstName || '',
        validation: {
          required: true,
        },
        touched: false,
        valid: !!editingProfile.FirstName,
      },
      lastname: {
        elementType: 'input',
        elementConfig: {
          type: 'text',
          placeholder: 'Last name*',
          readOnly: false,
        },
        value: editingProfile.LastName || '',
        validation: {
          required: true,
        },
        touched: false,
        valid: !!editingProfile.LastName,
      },
      nickname: {
        elementType: 'input',
        elementConfig: {
          type: 'text',
          placeholder: 'Nickname (optional)',
          readOnly: false,
        },
        value: editingProfile.NickName || '',
        validation: {
          required: false,
        },
        touched: false,
        valid: !!editingProfile.NickName,
      },
      birthdate: {
        elementType: 'input',
        elementConfig: {
          type: 'date',
          placeholder: 'Birthdate',
          guidetext: 'You birth date is used to build a log.',
          readOnly: false,
        },
        value: editingProfile.DateBirthDay || '',
        validation: {
          required: true,
        },
        touched: false,
        valid: !!editingProfile.DateBirthDay,
      },
      color: {
        elementType: 'input',
        elementConfig: {
          type: 'controlled',
          readOnly: true,
        },
        value: editingProfile.Color || '',
        validation: {
          required: true,
        },
        touched: false,
        valid: !!editingProfile.Color,
      },
    },
    profileThumbUrl: _get(editingProfile, 'ImageURL', _get(editingProfile, 'ThumbURL', '')),
    showFileTypeError: false,
    showColorSelector: false,
    showCloseFormDialogue: false,
    showBirthdatePicker: false,
    loadingImage: false,
    formIsValid: false,
    formTouched: false,
    attemptedFormSubmission: false,
    showDeletePostConfirmation: false,
  });
  const dispatch = useDispatch();
  const isManagedUser = signedInAs.UserId ? signedInAs.UserId !== editingProfile.UserId : false;
  const profileCreated = editingProfile.ProfileStatus !== 'NotComplete';

  const onProfileImageUpload = (profileImage: string) => dispatch(actions.uploadProfileImage(profileImage));
  const onProfileSubmit = (
    UserId: string,
    profileImageId: string,
    FirstName: string,
    LastName: string,
    NickName: string,
    DateBirthDay: string,
    Color: string,
    Phone: string,
    pc: boolean,
    im: boolean,
  ) => {
    const data = {
      UserId,
      profileImageId,
      FirstName,
      LastName,
      NickName,
      DateBirthDay,
      Color,
      Phone,
      profileCreated: pc,
      isManagedUser: im,
    };
    if (editingProfile && editingProfile.UserId) {
      return dispatch(submitProfile(data));
    }
    return dispatch(createManagedAccount(data));
  };
  const onDeleteManagedAccount = (manageUserId: string) => dispatch(actions.deleteManagedAccount(manageUserId));
  const onCloseProfileForm = () => dispatch(actions.closeProfileForm());

  const handleColorSelection = (colorName: string) => {
    const updatedControls: ProfileFormState['controls'] = updateObject(state.controls, {
      ...state.controls,
      color: updateObject(state.controls.color, {
        value: colorName,
        valid: true,
        touched: true,
      }),
    });
    let formIsValid = true;
    _forEach(updatedControls, (value) => {
      formIsValid = ((value.valid || !value.validation.required) && formIsValid) || state.formIsValid;
    });

    setState((prevState) => ({
      ...prevState,
      controls: updatedControls,
      formIsValid,
      formTouched: true,
    }));
    setTimeout(() => setState((prevState) => ({ ...prevState, showColorSelector: false })), 1000);
  };

  const handleImageUpload = (event: MouseEvent<HTMLInputElement>) => {
    const approvedFileTypes = ['image/jpeg', 'image/png'];
    if (_get(event, 'target.files.0') && approvedFileTypes.includes(_get(event, 'target.files.0.type', ''))) {
      onProfileImageUpload(_get(event, 'target.files.0'));
      setState((prevState) => ({ ...prevState, loadingImage: true, formTouched: true }));
    } else {
      setState((prevState) => ({ ...prevState, showFileTypeError: true }));
    }
  };

  const hideFileTypeErrorDialogue = () => {
    setState((prevState) => ({ ...prevState, showFileTypeError: false }));
  };

  const handleColorSelectorToggle = () => {
    setState((prevState) => ({ ...prevState, showColorSelector: !prevState.showColorSelector }));
  };

  const handleImageUpdate = useCallback(() => {
    const imageURL = _get(editingProfile, 'ImageURL', _get(editingProfile, 'ThumbURL', ''));
    if (imageURL) {
      const updatedControls: ProfileFormState['controls'] = updateObject(state.controls, {
        profileImageId: updateObject(state.controls.profileImageId, {
          value: imageURL,
          valid: true,
          touched: true,
        }),
      });
      let formIsValid = true;
      _forEach(updatedControls, (value) => {
        formIsValid = ((value.valid || !value.validation.required) && formIsValid) || state.formIsValid;
      });
      setState((prevState) => ({
        ...prevState,
        controls: updatedControls,
        formIsValid,
        profileThumbUrl: imageURL || '',
        loadingImage: false,
        formTouched: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingProfile.ImageURL, editingProfile.ThumbURL]);

  const handleDateSelect = (date: string) => {
    const birthdate = date || '';
    const updatedControls = updateObject(state.controls, {
      birthdate: updateObject(state.controls.birthdate, {
        value: birthdate,
        valid: !!birthdate,
        touched: true,
      }),
    });
    let formIsValid = true;
    _forEach(updatedControls, (value) => {
      formIsValid = ((value.valid || !value.validation.required) && formIsValid) || state.formIsValid;
    });
    setState((prevState) => ({ ...prevState, controls: updatedControls, formIsValid, formTouched: true, showBirthdatePicker: false }));
  };

  const inputChangedHandler = (event: { target: HTMLInputElement }, controlName: keyof ProfileFormState['controls']) => {
    const updatedControls = updateObject(state.controls, {
      [controlName]: updateObject(state.controls[controlName], {
        value: event.target.value,
        valid: checkValidity(event.target?.value, _get(state, `controls.${controlName}.validation`)),
        touched: true,
      }),
    });

    let formIsValid = true;
    _forEach(updatedControls, (value) => {
      formIsValid = ((value.valid || !value.validation.required) && formIsValid) || state.formIsValid;
    });

    setState((prevState) => ({ ...prevState, controls: updatedControls, formIsValid, formTouched: true }));
  };

  const submitHandler = () => {
    if (state.formIsValid) {
      onProfileSubmit(
        editingProfile.UserId || '',
        _get(state, 'controls.profileImageId.value'),
        _get(state, 'controls.firstname.value'),
        _get(state, 'controls.lastname.value'),
        _get(state, 'controls.nickname.value'),
        formatDateForApi(addCurrentTimeToDateString(_get(state, 'controls.birthdate.value'))) || '',
        _get(state, 'controls.color.value'),
        '',
        profileCreated,
        isManagedUser,
      );
    } else {
      setState((prevState) => ({ ...prevState, attemptedFormSubmission: true }));
    }
  };

  const toggleDeleteManagedUserConfirmation = () => {
    setState((prevState) => ({ ...prevState, showDeleteManagedUserConfirmation: !state.showDeletePostConfirmation }));
  };

  const handleManagedUserAccountDeletion = () => {
    onDeleteManagedAccount(editingProfile.UserId || '');
  };

  const toggleProfileCloseDialogue = () => {
    // If user is editing an existing profile and has not made any changes, simply close profile form
    // Otherwise, render one of two showCloseFormDialogue dialogues
    if (!state.showDeletePostConfirmation && profileCreated && !state.formTouched) {
      onCloseProfileForm();
    } else {
      setState((prevState) => ({ ...prevState, showCloseFormDialogue: !state.showCloseFormDialogue }));
    }
  };

  const handleProfileFormClose = () => {
    // If user confirms form closing and is creating a profile for a new managed user,
    // once account is deleted, profile form will automatically close
    if (isManagedUser && !profileCreated) {
      handleManagedUserAccountDeletion();
    } else if (profileCreated) {
      onCloseProfileForm();
    }
    // If users are creating their initial profile, we won't allow them to close out the profile form until their profile is created
    // else if (!profileCreated) {
    // }
    toggleProfileCloseDialogue();
  };

  const toggleBirthdatePicker = () => setState((prevState) => ({ ...prevState, showBirthdatePicker: !prevState.showBirthdatePicker }));

  const callbacks = {
    handleColorSelection,
    handleImageUpload,
    hideFileTypeErrorDialogue,
    handleColorSelectorToggle,
    handleImageUpdate,
    handleDateSelect,
    inputChangedHandler,
    submitHandler,
    toggleDeleteManagedUserConfirmation,
    handleManagedUserAccountDeletion,
    handleProfileFormClose,
    toggleProfileCloseDialogue,
    toggleBirthdatePicker,
  };

  return { state, callbacks, isManagedUser, profileCreated, editingProfile };
};

export default useProfileForm;
